import { useNavigate, useLocation } from 'react-router-dom';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { getNames } from 'country-list';
import PageTitle from './PageTitle';
import PageSubtitle from './PageSubtitle';
import FormField from './FormField';
import NavigationButtons from './NavigationButtons';
import formSchema from './formSchema';
import FileDropZone from './FileDropZone';
import StepProgress from './StepProgress';
import SummaryDetails from './SummaryDetails';

const CompanyForm = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false)
  const [domainCategory, setDomainCategory] = useState([
    { id: "1", name: "Food and Beverage" },
    { id: "2", name: "Law" },
    { id: "3", name: "Design" },
    { id: "4", name: "Building" },
  ])
  const axiosPrivate = useAxiosPrivate();

  const initialValues = {
    firstName: '',
    lastName: '',
    fullName: '',
    gender: '',
    workingExperience: '',
    companyName: '',
    companyCategory: '',
    companyRegNum: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    companySize: '',
    contactNumber: '',
    contactEmail: '',
    joinOrCreate: '',
    accessID: ''
  };

  //Error due to forbidden, maybe since the user is not logged in yet
  //To fetch the industries from database
  // useEffect(() => {
  //   let isMounted = true;
  //   const controller = new AbortController()

  //   const fetchCategories = async () => {
  //     setIsLoading(true)
  //     try {
  //       const response = await axiosPrivate.get('/template/get-industry',
  //         {
  //           signal: controller.signal,
  //         }
  //       )

  //       if (isMounted) {
  //         console.log(response.data)
  //         setDomainCategory(response.data);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching categories:', error);
  //     }

  //     setIsLoading(false)
  //   };

  //   fetchCategories();

  //   return () => {
  //     isMounted = false; // clean up if component unmounts
  //     controller.abort()
  //   };
  // }, [axiosPrivate]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const requiredFields =
      values.joinOrCreate === 'join'
        ? [
          'firstName',
          'lastName',
          'joinOrCreate',
          'gender',
          'accessID',
        ]
        : [
          'firstName',
          'lastName',
          'joinOrCreate',
          'companyName',
          'companyCategory',
          'companyRegNum',
        ];

    const emptyFields = requiredFields.filter((field) => !values[field]);

    if (emptyFields.length > 0) {
      // Handle empty fields (e.g., show an error message)
      console.error('Required fields are empty:', emptyFields);
      return;
    }

    const formData = new FormData();

    if (values.joinOrCreate === "create") {
      Object.keys(values).forEach((key) => {
        if (!['joinOrCreate', 'accessID'].includes(key)) {
          const value = values[key];

          if (value && value.name) {
            // Assuming file fields are directly files and not FileLists
            formData.append(key, value);
          } else {
            if (key == "companyCategory") {
              const domain = domainCategory.find(item => item.name.toLowerCase() === values["companyCategory"].toLowerCase());
              formData.append("domainId", domain.id)
            }
            else {
              formData.append(key, value);
            }
          }
        }
      });
    } else if (values.joinOrCreate === "join") {
      Object.keys(values).forEach((key) => {
        // Only include join-related fields
        const allowedJoinFields = [
          'firstName',
          'lastName',
          'fullName',
          'gender',
          'workingExperience',
          'accessID',
        ];

        if (allowedJoinFields.includes(key)) {
          const value = values[key];
          if (value && value.name) {
            // If it's a file (e.g., profile picture)
            formData.append(key, value);
          } else {
            formData.append(key, value);
          }
        }
      });
    }
    // console.log('Form is submitting...', formData);

    let isMounted = true;

    const controller = new AbortController();



    try {
      // Use axiosPrivate for submitting form data
      if (values.joinOrCreate === "create") {
        await axiosPrivate.post(
          '/upload/submit-form',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
          }
        );
      }
      else if (values.joinOrCreate === "join") {
        await axiosPrivate.post(
          '/upload/join-company',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
          }
        );
      }

      // Check if the component is still mounted before updating state
      if (isMounted) {
        if (values.joinOrCreate === "create"){
          navigate('/info-success', { state: { from: location }, replace: true });
        }
        else if (values.joinOrCreate === "join") {
          window.location.reload()
        }
        // Handle successful submission (e.g., showing a success message, navigating to another page)
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request cancelled:', err.message);
      } else {
        console.error('Submission failed:', err);
        // Handle errors (e.g., showing error message, navigating on certain conditions)
        if (err.response) {
          const statusCode = err.response.status;
          if (statusCode === 401) {
            // Token has expired, redirect to sign-in page
            navigate('/sign-in', { state: { from: location }, replace: true });
          } else {
            // Handle other errors here
            console.error('Other error occurred:', err.response.data);
          }
        }
      }
    }

    if (isMounted) {
      setSubmitting(false); // Ensure form is re-enabled
    }

    // Cleanup function to prevent memory leaks and unwanted behavior
    return () => {
      isMounted = false;
      controller.abort(); // Cancel the request if the component unmounts
    };
  }

  const renderFormPage = (formikProps, errors) => {
    switch (pageNumber) {
      case 1:
        return (
          <>
            <PageTitle>Personal Details</PageTitle>
            <PageSubtitle>Please fill in your personal details</PageSubtitle>
            {/* Using FormField component to render each field */}
            <FormField
              label="First Name"
              name="firstName"
              type="text"
              {...formikProps.getFieldProps('firstName')}
            />
            {errors.firstName ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.firstName}</span>
            ) : null}
            <FormField
              label="Last Name"
              name="lastName"
              type="text"
              {...formikProps.getFieldProps('lastName')}
            />
            {errors.lastName ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.lastName}</span>
            ) : null}
            <FormField
              label="Full Name"
              name="fullName"
              type="text"
              {...formikProps.getFieldProps('fullName')}
            />
            {errors.fullName ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.fullName}</span>
            ) : null}
            <FormField
              label="Gender"
              name="gender"
              type="select"
              {...formikProps.getFieldProps('gender')}
            >
              {/* Assuming gender options are predefined */}
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </FormField>
            {errors.gender ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.gender}</span>
            ) : null}
            <FormField
              label="Working Experience (Years)"
              name="workingExperience"
              type="select"
              {...formikProps.getFieldProps('workingExperience')}
            >
              <option value="">Select Experience</option>
              <option value="0-1">0-1 year</option>
              <option value="2-5">2-5 years</option>
              <option value="6-10">6-10 years</option>
              <option value="11-20">11-20 years</option>
              <option value="21+">21+ years</option>
            </FormField>
            {errors.workingExperience ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.workingExperience}</span>
            ) : null}
            <FormField
              label="Joining or Creating a Company?"
              name="joinOrCreate"
              type="select"
              {...formikProps.getFieldProps('joinOrCreate')}
            >
              <option value="">Select one</option>
              <option value="join">Join an existing company</option>
              <option value="create">Create a new company</option>
            </FormField>
            {errors.joinOrCreate ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.joinOrCreate}</span>
            ) : null}
          </>
        );
      case 1.5:
        return (
          <>
            <PageTitle>Joining an Existing Company</PageTitle>
            <PageSubtitle>Please fill in the company access ID</PageSubtitle>
            <FormField
              label="Access ID"
              name="accessID"
              type="text"
              {...formikProps.getFieldProps('accessID')}
            />
            {errors.accessID ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.accessID}</span>
            ) : null}
          </>
        )
      // case 2, case 3, etc., for other pages
      case 2:
        return (
          <>
            <PageTitle>Company Details</PageTitle>
            <PageSubtitle>Please fill in your company details</PageSubtitle>
            <FormField
              label="Company Name"
              name="companyName"
              type="text"
              {...formikProps.getFieldProps('companyName')}
            />
            {errors.companyName ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.companyName}</span>
            ) : null}
            <FormField
              label="Company Registration Number"
              name="companyRegNum"
              type="text"
              {...formikProps.getFieldProps('companyRegNum')}
            />
            {errors.companyRegNum ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.companyRegNum}</span>
            ) : null}
            <FormField
              label="Address Line 1"
              name="addressLine1"
              type="text"
              {...formikProps.getFieldProps('addressLine1')}
            />
            {errors.addressLine1 ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.addressLine1}</span>
            ) : null}
            <FormField
              label="Address Line 2"
              name="addressLine2"
              type="text"
              {...formikProps.getFieldProps('addressLine2')}
            />
            {errors.addressLine2 ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.addressLine2}</span>
            ) : null}
            <FormField
              label="City"
              name="city"
              type="text"
              {...formikProps.getFieldProps('city')}
            />
            {errors.city ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.city}</span>
            ) : null}
            <FormField
              label="State"
              name="state"
              type="text"
              {...formikProps.getFieldProps('state')}
            />
            {errors.state ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.state}</span>
            ) : null}
            <FormField
              label="Postal Code"
              name="postalCode"
              type="text"
              {...formikProps.getFieldProps('postalCode')}
            />
            {errors.postalCode ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.postalCode}</span>
            ) : null}
            <FormField
              label="Country"
              name="country"
              type="select"
              {...formikProps.getFieldProps('country')}
            >
              {/* Populate with your countries */}
              <option value="">Select Country</option>
              {getNames().map((countryName) => (
                <option key={countryName} value={countryName}>
                  {countryName}
                </option>
              ))}
              {/* Add more countries as needed */}
            </FormField>
            {errors.country ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.country}</span>
            ) : null}
            <FormField
              label="Company Size"
              name="companySize"
              type="select"
              {...formikProps.getFieldProps('companySize')}
            >
              <option value="">Select Size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-100">51-100 employees</option>
              <option value="101-300">101-300 employees</option>
              <option value="301-1000">301-1000 employees</option>
              <option value="1000+">1000+ employees</option>
              {/* Add more size options as needed */}
            </FormField>
            {errors.companySizey ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.companySizey}</span>
            ) : null}
            <FormField
              label="Company Category"
              name="companyCategory"
              type="select"
              {...formikProps.getFieldProps('companyCategory')}
            >
              <option value="">Select Category</option>
              {domainCategory.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </FormField>
            {errors.companyCategory ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.companyCategory}</span>
            ) : null}
            <FormField
              label="Contact Number"
              name="contactNumber"
              type="tel"
              {...formikProps.getFieldProps('contactNumber')}
            />
            {errors.contactNumber ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.contactNumber}</span>
            ) : null}
            <FormField
              label="Contact Email"
              name="contactEmail"
              type="email"
              {...formikProps.getFieldProps('contactEmail')}
            />
            {errors.contactEmail ? (
              <span className='text-red-500 mt-0 pt-0'>{errors.contactEmail}</span>
            ) : null}
          </>
        );
      case 3:
        return (
          <>
            <PageTitle>SSM Certificate</PageTitle>
            <PageSubtitle>
              Submit your SSM registered certificate for verification
            </PageSubtitle>
            <FileDropZone name="ssmCertificate" />
          </>
        );
      case 4:
        // Summary page for review
        return <SummaryDetails formikProps={formikProps} />;
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <>
      <StepProgress pageNumber={pageNumber} />
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const { errors } = formikProps;

          return (
            <Form>
              {renderFormPage(formikProps, errors)}

              <NavigationButtons
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                isLastPage={(pageNumber === 4 || pageNumber === 1.5)}
                joinOrCreate={formikProps.values.joinOrCreate}
                {...formikProps}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CompanyForm;
